import React, { useEffect, useState } from "react";
import moment from "moment";
import { useRouteMatch, Link, useParams } from "react-router-dom";
import { useAuthState } from "../../../../components/AuthProvider";
import formattedDate from "../../../../utils/formattedDate";
import Container from "../../../../components/Container";
import Content from "../../../../components/Content";
import DataTable, {
  DropDownListFilter,
  SelectInputFilter
} from "../../../../components/DataTable";
import Flex from "../../../../components/Flex";
import _groupBy from "lodash/groupBy";
import Layout from "../../../../components/Layout";
import DefaultBreadcrumbs from "../../../../components/DefaultBreadcrumbs";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Breadcrumb from "../../../../components/Breadcrumbs";
import ButtonList from "../../../../components/ButtonList";
import Button from "../../../../components/Button";
import LinkButton from "../../../../components/LinkButton";
import AnchorButton from "../../../../components/AnchorButton";
import { useHistory } from "react-router-dom";
import Icon from "../../../../components/Icon";
import { checkReadOnlyUser } from "../../../../utils/checkReadOnlyUser.js";
import { formattedDateWithDefault } from "../../../../utils/formattedDate";
import { isImdmClient } from "../../../../utils/isImdmClient";
import { useMessageToast } from "../../../../components/MessageProvider";
import styles from "./LinkageHistory.module.scss";
import Spinner from "../../../../components/Loader/Spinner";
import handleResponse from "../../../../utils/handleFetchResponse";
import fetchConfig from "../../../../utils/fetchConfig";

const LinkageHistory = () => {
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const isImdm = isImdmClient(selectedClient);

  const { clientId, goldenId } = useParams();

  const [linkageHistory, setLinkageHistory] = React.useState([]);

  const toast = useMessageToast();
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [sourcesFilter, setSourcesFilter] = React.useState([]);
  const [actionsFilter, setActionsFilter] = React.useState([]);

  const createSourcesFilter = linkageHistory => {
    const linkageSources = linkageHistory.map(
      link => link.recordId.split("|")[1]
    );
    const uniqueLinkageSources = [...new Set(linkageSources)];
    const sources = uniqueLinkageSources.map(source => ({
      text: source,
      value: source
    }));
    setSourcesFilter(sources);
  };

  const actionsTranslation = {
    A: "Record Added",
    N: "New Record",
    RD: "Record Deleted",
    S: "Split",
    M: "Merge",
    AD: "Aggregate Deleted"
  };

  const createActionsFilter = linkageHistory => {
    const linkageActions = linkageHistory.map(link => link.action);
    const uniqueLinkageActions = [...new Set(linkageActions)];
    const actions = uniqueLinkageActions.map(action => ({
      text: actionsTranslation[action],
      value: actionsTranslation[action]
    }));
    setActionsFilter(actions);
  };

  const newAggCell = ({
    cell: {
      value,
      row: { original: selectedAgg }
    }
  }) => {
    const onViewClick = () => {
      history.push(
        `/search-identities-new/${clientId}/view/${selectedAgg.newAggId}`
      );
    };
    return (
      <AnchorButton
        to={{
          pathname: `/search-identities-new/${clientId}/view/${selectedAgg.newAggId}`
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {selectedAgg.newAggId}
      </AnchorButton>
    );
  };

  const prevAggCell = ({
    cell: {
      value,
      row: { original: selectedAgg }
    }
  }) => {
    const onViewClick = () => {
      history.push(
        `/search-identities-new/${clientId}/view/${selectedAgg.prevAggId}`
      );
    };
    return (
      <AnchorButton
        to={{
          pathname: `/search-identities-new/${clientId}/view/${selectedAgg.prevAggId}`
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        {selectedAgg.prevAggId}
      </AnchorButton>
    );
  };

  const linkageHistoryCsvColumns = [
    {
      Header: "Record ID",
      accessor: "recordId"
    },
    {
      Header: "Action Code",
      accessor: "action"
    },
    {
      Header: "Epoch Event Timestamp",
      accessor: "eventTimestamp"
    },
    {
      Header: "New Aggregate ID",
      accessor: "newAggId"
    },
    {
      Header: "Previous Aggregate ID",
      accessor: "prevAggId"
    },
    {
      Header: "PersonID",
      accessor: "personId"
    },
    {
      Header: "Last Updated By",
      accessor: "correlationId"
    }
  ];

  const linkageHistoryColumns = [
    {
      Header: "Record ID",
      accessor: "recordId"
    },
    {
      Header: "Source System",
      accessor: row => row.recordId.split("|")[1],
      Filter: SelectInputFilter,
      filter: SelectInputFilter.filter,
      filterProps: { items: sourcesFilter }
    },
    {
      Header: "Action",
      accessor: row => actionsTranslation[row.action],
      Filter: SelectInputFilter,
      filter: SelectInputFilter.filter,
      filterProps: { items: actionsFilter }
    },
    {
      Header: "Event Timestamp",
      accessor: row => formattedDateWithDefault(parseInt(row.eventTimestamp))
    },
    {
      Header: "New Aggregate ID",
      accessor: "newAggId",
      Cell: newAggCell
    },
    {
      Header: "Previous Aggregate ID",
      accessor: "prevAggId",
      Cell: prevAggCell
    },
    {
      Header: "PersonID",
      accessor: "personId"
    },
    {
      Header: "Last Updated By",
      accessor: "correlationId"
    }
  ];

  const fetchLinkageHistory = React.useCallback(() => {
    setIsLoading(true);
    const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/linkage/${goldenId}/history`;
    const mockUrl = `/getLinkageHistory.json`;
    const config = {
      credentials: "include",
      headers: {
        clientId: clientId,
        "Content-Type": "application/json",
        userId: user.username
      }
    };
    fetch(...fetchConfig(url, mockUrl, config))
      .then(handleResponse)
      .then(response => {
        const sortedData = response.data.sort((a, b) => {
          return a.eventTimestamp < b.eventTimestamp ? 1 : -1;
        });
        setLinkageHistory(sortedData);
        createSourcesFilter(response.data);
        createActionsFilter(response.data);
        setIsLoading(false);
      })
      .catch(e => {
        setIsLoading(false);
        toast.error("Something went wrong while fetching the linkage history");
      });
  }, [toast, clientId, goldenId]);

  useEffect(() => {
    if (!hasLoaded) {
      setIsLoading(true);
      fetchLinkageHistory();
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [setIsLoading, setHasLoaded, hasLoaded, linkageHistory]);

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12} className={styles.base}>
            <Content>
              <div style={{ width: "100%" }}>
                <div style={{ width: "50%", float: "left" }}>
                  <h1>Linkage History</h1>
                  <p>
                    Full linkage history details for Aggregate ID: {goldenId}
                  </p>
                </div>
              </div>
            </Content>
          </Flex.Item>

          <Flex.Item span={12} className={styles.base}>
            <Content>
              <div>
                <DataTable
                  columns={linkageHistoryColumns}
                  csvColumns={linkageHistoryCsvColumns}
                  data={linkageHistory}
                  hasPagination
                  hasFiltering
                  hasDownloadCsv={true}
                />
              </div>
            </Content>
          </Flex.Item>
        </Flex>
      </Container>
    </Layout>
  );
};

export default LinkageHistory;
